import { useEffect, useRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { PROJECTS, CATEGORIES } from "@/data/site";
import { Reveal, Eyebrow } from "./Reveal";
import { ProjectDialog } from "./ProjectDialog";
import { EASE, useIsTouch } from "./motion";

// Desktop: card tilts toward the cursor like a model being turned in hand. Touch: press feedback only.
const TiltCard = ({ p, i, onOpen, touch }) => {
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [6, -6]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(mx, [0, 1], [-6, 6]), { stiffness: 200, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };
  return (
    <motion.button
      onClick={() => onOpen(p)}
      onPointerMove={touch ? undefined : onMove}
      onPointerLeave={touch ? undefined : reset}
      style={touch ? undefined : { rotateX: rx, rotateY: ry, transformPerspective: 900 }}
      whileTap={{ scale: 0.97 }}
      variants={{ hidden: { clipPath: "inset(100% 0 0 0)", y: 24 }, show: { clipPath: "inset(0% 0 0 0)", y: 0 } }}
      transition={{ duration: 0.9, ease: EASE }}
      data-testid={`project-card-${p.id}`}
      className={`group text-left aspect-[4/5] relative overflow-hidden bg-stone-200 shrink-0 w-[82vw] sm:w-auto snap-center ${i % 3 === 1 ? "lg:translate-y-12" : ""}`}
    >
      <img src={p.image} alt={p.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
      <span aria-hidden="true" className="absolute inset-3 border border-white/0 group-hover:border-white/50 transition-colors duration-500" />
      <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between text-white">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/80">{p.category}</p>
          <h3 className="font-serif text-2xl lg:text-3xl mt-1">{p.title}</h3>
        </div>
        <span className="w-10 h-10 rounded-full border border-white/60 flex items-center justify-center transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight size={16} /></span>
      </div>
    </motion.button>
  );
};

export const Projects = () => {
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const [page, setPage] = useState(0);
  const track = useRef(null);
  const touch = useIsTouch();
  const list = cat === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === cat);

  useEffect(() => {
    const el = track.current;
    if (!el) return;
    const onScroll = () => setPage(Math.round(el.scrollLeft / (el.firstElementChild?.offsetWidth + 24 || 1)));
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [cat]);

  return (
    <section id="projects" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-36 overflow-hidden" data-testid="projects-section">
      <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
        <div>
          <Eyebrow className="mb-6">02 / Selected Work</Eyebrow>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-stone-900">Projects</h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="projects-filter">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`filter-${c.toLowerCase()}`}
              className={`relative px-4 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border transition-colors duration-300 ${cat === c ? "text-[#FBFBF9] border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900"}`}>
              {cat === c && <motion.span layoutId="filter-pill" className="absolute inset-0 bg-stone-900" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
              <span className="relative">{c}</span>
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div
          key={cat}
          ref={track}
          className="projects-track flex overflow-x-auto snap-x snap-mandatory gap-6 -mx-6 px-6 pb-2 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 lg:gap-8 sm:overflow-visible"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          exit={{ opacity: 0, y: 12, transition: { duration: 0.25 } }}
          transition={{ staggerChildren: 0.12 }}
          data-testid="projects-grid"
        >
          {list.map((p, i) => <TiltCard key={p.id} p={p} i={i} onOpen={setActive} touch={touch} />)}
        </motion.div>
      </AnimatePresence>

      <div className="mt-6 flex items-center justify-center gap-2 sm:hidden" data-testid="projects-dots">
        {list.map((p, i) => (
          <span key={p.id} className={`h-1 transition-all duration-300 ${i === page ? "w-8 bg-stone-900" : "w-3 bg-stone-300"}`} />
        ))}
        <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">Swipe</span>
      </div>

      <motion.a whileTap={{ scale: 0.97 }} href="https://instagram.com/gokul.kv" target="_blank" rel="noopener noreferrer" className="btn-ghost mt-20" data-testid="projects-instagram-btn">More on Instagram @gokul.kv <ArrowUpRight size={16} /></motion.a>
      <ProjectDialog project={active} onClose={() => setActive(null)} />
    </section>
  );
};
